export const EMBRAER_COLUMN_ALIASES = {
  dataViagem: ["Data da Viagem (inicial)", "Data da Viagem (Inicial)", "Data da Viagem"],
  numeroProgramacao: ["Número Programação", "Numero Programacao", "Nº Programação"],
  tipoServico: ["Tipo Serviço", "Tipo Servico"],
  horario: ["Horário Passageiro", "Horario Passageiro"],
  nomePassageiro: ["Nome Passageiro"],
  chapa: ["Chapa"],
  telefone: ["Tel Cel.", "Tel Cel", "Tel. Cel."],
  origem: ["Origem"],
  cidadeOrigem: ["Cidade Origem", "Cidade origem"],
  destino: ["Destino"],
  cidadeDestino: ["Cidade Destino", "Cidade destino"],
  statusProgramacao: ["Status", "Status Programação", "Status Programacao", "Situação", "Situacao"],
};

export const EMBRAER_REQUIRED_COLUMNS = [
  "dataViagem",
  "numeroProgramacao",
  "tipoServico",
  "horario",
  "nomePassageiro",
  "chapa",
  "telefone",
  "origem",
  "destino",
];

const normalizeHeader = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .replace(/\s+/g, " ")
  .trim();

export const findEmbraerColumnIndex = (headers, fieldKey) => {
  const aliases = EMBRAER_COLUMN_ALIASES[fieldKey] || [];
  const normalizedAliases = new Set(aliases.map(normalizeHeader));

  return headers.findIndex((header) => normalizedAliases.has(normalizeHeader(header)));
};

export const mapEmbraerHeaders = (headerRow = []) => {
  const headers = headerRow.map((value) => String(value ?? "").trim());
  const mapping = {};

  for (const fieldKey of Object.keys(EMBRAER_COLUMN_ALIASES)) {
    const index = findEmbraerColumnIndex(headers, fieldKey);
    if (index >= 0) mapping[fieldKey] = index;
  }

  return { headers, mapping };
};

const normalizeCidadeEmbraer = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .replace(/\s+/g, " ")
  .trim();

/** Origem na fábrica (Gavião Peixoto) = volta para casa; demais origens = ida para Embraer. */
export const isOrigemGaviaoPeixoto = ({ origem, cidadeOrigem } = {}) => {
  const candidatos = [cidadeOrigem, origem].filter(Boolean);
  return candidatos.some((valor) => normalizeCidadeEmbraer(valor).includes("gaviao peixoto"));
};

export const inferTrechoFromOrigem = ({ origem, cidadeOrigem } = {}) => (
  isOrigemGaviaoPeixoto({ origem, cidadeOrigem }) ? "volta" : "ida"
);

/** @deprecated Use inferTrechoFromOrigem — mantido só para compatibilidade. */
export const inferTrechoFromTipoServico = inferTrechoFromOrigem;

export const normalizeEmbraerNome = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .replace(/\s+/g, " ")
  .trim();

export const normalizeEmbraerTelefone = (value) => {
  let digits = String(value || "").replace(/\D/g, "");
  if (digits.startsWith("55") && digits.length > 11) digits = digits.slice(-11);
  return digits.slice(-11) || digits;
};

export const parseEmbraerDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }

  const text = String(value).trim();
  const brMatch = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (brMatch) {
    return `${brMatch[3]}-${brMatch[2]}-${brMatch[1]}`;
  }

  const iso = text.substring(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  return null;
};

export const parseEmbraerHorario = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toTimeString().slice(0, 5);
  }

  const text = String(value).trim();
  const match = text.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return `${match[1].padStart(2, "0")}:${match[2]}`;
};

export const formatTrechoLabel = (trecho, destinoLabel = "Destino") => (
  trecho === "volta" ? "Volta → Casa" : `Ida → ${destinoLabel}`
);

/** Embraer exporta PG2026/41958; no RotaFlex cadastramos 2026/41958. */
export const normalizeEmbraerIdPedido = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  return text.replace(/^PG\s*/i, "");
};

const normalizeEmbraerStatus = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .replace(/\s+/g, " ")
  .trim();

/** Programações recusadas pela central Embraer não entram na auditoria. */
export const isEmbraerStatusRecusadoPelaCentral = (value) => (
  normalizeEmbraerStatus(value) === "recusado pela central"
);

/** Uma programação = id + data + trecho (vários horários de passageiro na mesma viagem). */
export const buildEmbraerProgramacaoKey = ({ id_pedido, data, trecho }) => (
  [id_pedido, data || "", trecho].join("|")
);

export const collectEmbraerHorarios = (source = {}) => {
  const horarios = new Set();

  for (const value of source.horarios || []) {
    const parsed = parseEmbraerHorario(value);
    if (parsed) horarios.add(parsed);
  }

  const principal = parseEmbraerHorario(source.horario);
  if (principal) horarios.add(principal);

  for (const passageiro of source.passageiros || []) {
    const parsed = parseEmbraerHorario(passageiro.horario);
    if (parsed) horarios.add(parsed);
  }

  return [...horarios].sort();
};

export const formatEmbraerHorarios = (source = {}) => {
  const horarios = collectEmbraerHorarios(source);
  return horarios.length ? horarios.join(" · ") : "—";
};
